/* global grecaptcha */
import React, {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import injectSheet from 'react-jss';
import { Typography } from 'antd';
import noop from 'lodash/noop';
import classnames from 'classnames';
import firebase, { signInWithPhoneNumber } from '../../../lib/firebase';
import routes from '../../../routes';
import { classesPropType, historyPropType } from '../../prop-types';
import ValidationCodeForm from './ValidationCodeForm';

const { Text } = Typography;

const styles = {
  explanation: {
    display: 'block',
    textAlign: 'center',
    marginBottom: '10px',
  },
  explanationBottom: {
    marginBottom: '30px',
  },
};

function PhoneValidation({
  classes,
  history: { replace },
  inviterName,
  mandatory,
  onSubmitWithAuth,
  onSignIn,
  phoneNumber,
}) {
  const { t } = useTranslation();
  const [confirmHandler, setConfirmHandler] = useState();
  const [verificationUserId, setVerificationUserId] = useState();
  const [sendingSMS, setSendingSMS] = useState(false);
  const appVerifier = useRef();
  const recaptchaVerifier = useRef();

  useLayoutEffect(
    () => {
      recaptchaVerifier.current = new firebase.auth.RecaptchaVerifier(
        appVerifier.current,
        { size: 'invisible' },
      );
    },
    [],
  );

  const handleSMSButtonPress = useCallback(
    () => {
      setSendingSMS(true);
      return signInWithPhoneNumber(phoneNumber, recaptchaVerifier.current)
        .then((confirmResult) => {
          onSignIn();
          setConfirmHandler(confirmResult);
          setSendingSMS(false);
        })
        .catch(() => setSendingSMS(false));
    },
    [phoneNumber, onSignIn],
  );

  const handleSubmit = useCallback(
    (confirmCode) => {
      if (verificationUserId) {
        return onSubmitWithAuth(verificationUserId);
      }

      return confirmHandler.confirm(confirmCode)
        .then(({ user: { uid } }) => {
          setVerificationUserId(uid);
          return onSubmitWithAuth(uid);
        })
        .catch(async (error) => {
          await recaptchaVerifier.current.render()
            .then(widgetId => grecaptcha.reset(widgetId))
            .catch(noop);
          throw error;
        });
    },
    [confirmHandler, onSubmitWithAuth, verificationUserId],
  );

  const handleSkipClick = useCallback(() => replace(routes.thankYou), [replace]);

  // Handle case in which someone enters this URL directly in browser
  if (!phoneNumber || !inviterName) return <Redirect to={routes.download} />;

  const explanation = mandatory
    ? (
      <Text className={classnames(classes.explanation, classes.explanationBottom)}>
        {t('phoneValidation.explanation.mandatory')}
      </Text>
    )
    : (
      <>
        <Text className={classes.explanation}>
          {t('phoneValidation.explanation.voluntaryBeginning', { inviterName })}
        </Text>
        <Text className={classnames(classes.explanation, classes.explanationBottom)}>
          {t('phoneValidation.explanation.voluntaryEnding', { inviterName })}
        </Text>
      </>
    );

  return (
    <>
      {explanation}

      <ValidationCodeForm
        confirmHandler={confirmHandler}
        mandatory={mandatory}
        onSendSMSClick={handleSMSButtonPress}
        onSkipClick={handleSkipClick}
        onSubmit={handleSubmit}
        phoneNumber={phoneNumber}
        replace={replace}
        sendingSMS={sendingSMS}
      />

      <div ref={appVerifier} />
    </>
  );
}

PhoneValidation.propTypes = {
  classes: classesPropType.isRequired,
  history: historyPropType.isRequired,
  inviterName: PropTypes.string.isRequired,
  mandatory: PropTypes.bool,
  onSignIn: PropTypes.func.isRequired,
  onSubmitWithAuth: PropTypes.func.isRequired,
  phoneNumber: PropTypes.string.isRequired,
};

PhoneValidation.defaultProps = {
  mandatory: false,
};

export default injectSheet(styles)(PhoneValidation);
